const GRID_COLUMNS = 16;
const GRID_ROWS = 9;
const MAX_SAMPLES = 220_000;
const clamp01 = (value) => Math.max(0, Math.min(1, value));
const round = (value, digits = 4) => Number(value.toFixed(digits));
function strongScreenPixel(type, r, g, b) {
    if (type === 'green')
        return g >= 70 && g - Math.max(r, b) >= 50 && g >= r * 1.35 && g >= b * 1.2;
    if (type === 'blue')
        return b >= 70 && b - Math.max(r, g) >= 50 && b >= r * 1.35 && b >= g * 1.15;
    return false;
}
function fallbackScreenType(frame, stride) {
    let green = 0;
    let blue = 0;
    let total = 0;
    for (let y = 0; y < frame.height; y += stride) {
        for (let x = 0; x < frame.width; x += stride) {
            const offset = (y * frame.width + x) * 4;
            const r = frame.data[offset] ?? 0;
            const g = frame.data[offset + 1] ?? 0;
            const b = frame.data[offset + 2] ?? 0;
            total++;
            if (strongScreenPixel('green', r, g, b))
                green++;
            if (strongScreenPixel('blue', r, g, b))
                blue++;
        }
    }
    if (Math.max(green, blue) / Math.max(1, total) < 0.12)
        return 'unknown';
    return green > blue ? 'green' : blue > green ? 'blue' : 'unknown';
}
function screenTypeFromContext(context) {
    const screenResult = context.analyzerResults.get('screen-quality');
    if (!screenResult || typeof screenResult.metrics !== 'object' || screenResult.metrics === null)
        return undefined;
    const type = screenResult.metrics.screen_type;
    return type === 'green' || type === 'blue' || type === 'unknown' ? type : undefined;
}
export class SpillDetectionAnalyzer {
    id = 'spill-detection';
    label = 'Spill Detection';
    analyze(frame, context) {
        const { width, height, data } = frame;
        const stride = Math.max(1, Math.ceil(Math.sqrt((width * height) / MAX_SAMPLES)));
        const screenType = screenTypeFromContext(context) ?? fallbackScreenType(frame, stride);
        const cellHits = new Array(GRID_COLUMNS * GRID_ROWS).fill(0);
        const cellStrength = new Array(GRID_COLUMNS * GRID_ROWS).fill(0);
        let sampled = 0;
        let foreground = 0;
        let edgeCandidates = 0;
        let affected = 0;
        let strengthSum = 0;
        let minX = width;
        let minY = height;
        let maxX = 0;
        let maxY = 0;
        const neighborDistance = Math.max(1, stride * 2);
        const neighbors = [[-neighborDistance, 0], [neighborDistance, 0], [0, -neighborDistance], [0, neighborDistance]];
        if (screenType !== 'unknown') {
            for (let y = 0; y < height; y += stride) {
                for (let x = 0; x < width; x += stride) {
                    const offset = (y * width + x) * 4;
                    const r = data[offset] ?? 0;
                    const g = data[offset + 1] ?? 0;
                    const b = data[offset + 2] ?? 0;
                    sampled++;
                    if (strongScreenPixel(screenType, r, g, b))
                        continue;
                    foreground++;
                    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
                    let likelyEdge = false;
                    for (const [dx, dy] of neighbors) {
                        const nx = x + dx;
                        const ny = y + dy;
                        if (nx < 0 || nx >= width || ny < 0 || ny >= height)
                            continue;
                        const neighborOffset = (ny * width + nx) * 4;
                        const nr = data[neighborOffset] ?? 0;
                        const ng = data[neighborOffset + 1] ?? 0;
                        const nb = data[neighborOffset + 2] ?? 0;
                        if (!strongScreenPixel(screenType, nr, ng, nb))
                            continue;
                        const neighborLuma = 0.2126 * nr + 0.7152 * ng + 0.0722 * nb;
                        const chromaContrast = Math.hypot(r - nr, g - ng, b - nb);
                        if (Math.abs(luma - neighborLuma) >= 10 || chromaContrast >= 35) {
                            likelyEdge = true;
                            break;
                        }
                    }
                    if (!likelyEdge)
                        continue;
                    edgeCandidates++;
                    const dominance = screenType === 'green' ? g - (r + b) / 2 : b - (r + g) / 2;
                    const spillStrength = clamp01((dominance - 6) / 54);
                    if (spillStrength < 0.12)
                        continue;
                    affected++;
                    strengthSum += spillStrength;
                    minX = Math.min(minX, x);
                    minY = Math.min(minY, y);
                    maxX = Math.max(maxX, x);
                    maxY = Math.max(maxY, y);
                    const column = Math.min(GRID_COLUMNS - 1, Math.floor((x / width) * GRID_COLUMNS));
                    const row = Math.min(GRID_ROWS - 1, Math.floor((y / height) * GRID_ROWS));
                    const cellIndex = row * GRID_COLUMNS + column;
                    cellHits[cellIndex] = (cellHits[cellIndex] ?? 0) + 1;
                    cellStrength[cellIndex] = (cellStrength[cellIndex] ?? 0) + spillStrength;
                }
            }
        }
        const affectedEdgeRatio = affected / Math.max(1, edgeCandidates);
        const meanStrength = strengthSum / Math.max(1, affected);
        const spillScore = affected === 0 ? 0 : clamp01(affectedEdgeRatio * 0.65 + meanStrength * 0.35);
        const spillRisk = spillScore >= 0.55 ? 'high' : spillScore >= 0.2 ? 'medium' : 'low';
        const dominantSpillColor = affected === 0 || screenType === 'unknown' ? 'none' : screenType;
        const affectedAreaPercentage = affected / Math.max(1, sampled) * 100;
        const warnings = [];
        const addWarning = (message) => {
            warnings.push({
                code: message.startsWith('Green') ? 'green_spill_detected'
                    : message.startsWith('Blue') ? 'blue_spill_detected' : 'subject_screen_distance',
                severity: 'warning',
                message,
                module_id: this.id,
                frame_number: context.frameNumber,
            });
        };
        if (affected > 0 && screenType === 'green')
            addWarning('Green spill detected on subject edge');
        if (affected > 0 && screenType === 'blue')
            addWarning('Blue spill detected on subject edge');
        if (affected > 0 && (spillRisk === 'high' || affectedEdgeRatio >= 0.5)) {
            addWarning('Possible subject too close to screen');
        }
        const heatmapColor = screenType === 'blue' ? '#4d8dff' : '#55ff72';
        const overlays = cellHits.flatMap((hits, index) => {
            if (!hits)
                return [];
            const column = index % GRID_COLUMNS;
            const row = Math.floor(index / GRID_COLUMNS);
            return [{
                    type: 'heatmap',
                    points: [
                        { x: column / GRID_COLUMNS, y: row / GRID_ROWS },
                        { x: (column + 1) / GRID_COLUMNS, y: (row + 1) / GRID_ROWS },
                    ],
                    label: 'Spill heatmap',
                    color: heatmapColor,
                    opacity: round(0.25 + 0.6 * ((cellStrength[index] ?? 0) / hits)),
                    stroke_width: 1,
                    fill: true,
                }];
        });
        if (affected > 0) {
            overlays.push({
                type: 'box',
                points: [
                    { x: minX / width, y: minY / height },
                    { x: Math.min(1, (maxX + stride) / width), y: Math.min(1, (maxY + stride) / height) },
                ],
                label: 'Affected edge zones',
                color: '#ff9f43',
                opacity: 0.9,
                stroke_width: 3,
            });
        }
        return {
            module_id: this.id,
            module_name: this.label,
            confidence: screenType === 'unknown' ? 0.25 : round(clamp01(0.55 + Math.min(0.4, edgeCandidates / Math.max(1, foreground)))),
            frame_number: context.frameNumber,
            timestamp_ms: context.timestampMs,
            metrics: {
                spill_risk: spillRisk,
                spill_score: round(spillScore),
                affected_area_percentage: round(affectedAreaPercentage, 2),
                dominant_spill_color: dominantSpillColor,
            },
            warnings,
            overlays,
        };
    }
}
//# sourceMappingURL=SpillDetectionAnalyzer.js.map